import { FC, useMemo, useState } from "react";
import {
  Boosting,
  BoostingCampaign,
  LinkedBoostingCampaign,
} from "@vviinn/user-managment-client-ts";
import { useAppSelector } from "../../store";
import { Fields } from "../../ui/layout/detailedPages/Fields";
import { SlideOverFieldNamedRow } from "../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverSingleField/SlideOverFieldNamedRow";
import { FieldsGroupWrapper } from "../../ui/layout/organisms/SlideOver/SlideOverFixedLayouts/SlideOverFieldsGroups/FieldsGroupWrapper";
import { LinkedBoostingSlideOver } from "../boosting/components/LinkedBoostingSlideOver";
import { LimitNumberField } from "../boosting/components/LimitNumberField";
import { boostingNumberProps } from "../boosting/constants";
import { UpdateCampaignProps } from "../details/types";
import { parseNumberOrZero } from "../../helpers";

export const LinkedBoosting: FC<UpdateCampaignProps> = ({
  campaign,
  updateCampaignBody,
}) => {
  const allBoostingCampaigns = useAppSelector(
    (state) => state.boosting.boosting,
  );
  const customFields = useAppSelector((state) => state.site.customFields);
  const filters = useAppSelector((state) => state.campaigns.filters);
  const customFieldsTitles = useAppSelector(
    (state) => state.site.customFieldsTitles,
  );
  const columnOptions = useMemo(
    () => [...filters, ...customFieldsTitles],
    [filters, customFieldsTitles],
  );

  const currentBoosting: Boosting = useMemo(
    () =>
      campaign.boosting ?? { boostingWeight: 0, linkedBoostingCampaigns: [] },
    [campaign.boosting],
  );
  const linkedCampaigns = useMemo(
    () => currentBoosting.linkedBoostingCampaigns ?? [],
    [currentBoosting.linkedBoostingCampaigns],
  );

  const [config, setConfig] =
    useState<LinkedBoostingCampaign[]>(linkedCampaigns);
  const isSameConfig = useMemo(
    () =>
      config.length === linkedCampaigns.length &&
      config.every(
        (link, i) =>
          link.campaign.id === linkedCampaigns[i].campaign.id &&
          link.weight === linkedCampaigns[i].weight,
      ),
    [config, linkedCampaigns],
  );
  const allowSubmit = !isSameConfig;
  const [slideOverOpen, setSlideOverOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isNewDraft, setIsNewDraft] = useState(false);

  const currentLink = editingIndex !== null ? config[editingIndex] : undefined;

  const getAvailableForNewDraft = (): BoostingCampaign[] => {
    const usedIds = new Set(
      config.map((l) => l.campaign.id).filter((id): id is string => !!id),
    );
    return allBoostingCampaigns.filter(({ id }) => !!id && !usedIds.has(id));
  };

  const patchBoosting = async (patch: Partial<Boosting>): Promise<boolean> => {
    const merged: Boosting = { ...currentBoosting, ...patch };
    return await updateCampaignBody({ boosting: merged });
  };

  const openSlideOver = (index: number | null) => {
    setEditingIndex(index);
    setIsNewDraft(index === null);
    setSlideOverOpen(true);
  };

  const handleWeightChange = (index: number, weight: number) => {
    setConfig((prev) =>
      prev.map((l, i) => (i === index ? { ...l, weight } : l)),
    );
  };

  // FieldsGroupWrapper callbacks
  const addFieldHandler = () => openSlideOver(null);

  const removeFieldHandler = (i: number) => {
    setConfig((prev) => prev.filter((_, idx) => idx !== i));
  };

  const submitFieldHandler = async (): Promise<boolean> =>
    patchBoosting({ linkedBoostingCampaigns: config });

  const initConfig = () => setConfig(linkedCampaigns);

  const closeSlideOver = () => {
    setSlideOverOpen(false);
    setIsNewDraft(false);
    setEditingIndex(null);
  };

  const applyListPatch = async (
    updatedList: LinkedBoostingCampaign[],
  ): Promise<boolean> => {
    const success = await patchBoosting({
      linkedBoostingCampaigns: updatedList,
    });
    if (success) setConfig(updatedList);
    return success;
  };

  const handleNewDraftSubmit = async (
    link: LinkedBoostingCampaign,
  ): Promise<boolean> => {
    setConfig((prev) => [...prev, link]);
    return true;
  };

  // LinkedBoostingSlideOver callbacks — existing (BFF)
  const handleExistingUpdate = async (
    link: LinkedBoostingCampaign,
  ): Promise<boolean> => {
    if (editingIndex === null) return false;
    return applyListPatch(
      config.map((l, i) => (i === editingIndex ? link : l)),
    );
  };

  const handleCreateCopy = async (
    link: LinkedBoostingCampaign,
  ): Promise<boolean> => {
    if (editingIndex === null) return false;
    return applyListPatch(
      config.map((l, i) =>
        i === editingIndex
          ? { ...link, campaign: { ...link.campaign, id: undefined } }
          : l,
      ),
    );
  };

  const fieldRender = config.map((draft, i) => [
    {
      key: "name",
      title: "Name",
      value: draft.campaign.name,
      customId: `lb-name-${i}`,
      disabled: true,
      onChange: () => {},
    },
    {
      key: "weight",
      title: "Weight",
      value: draft.weight,
      customId: `lb-weight-${i}`,
      ...boostingNumberProps,
      onChange: (target: { value: string }) =>
        handleWeightChange(i, parseNumberOrZero(target.value)),
    },
    {
      key: "column",
      title: "Column",
      value: draft.campaign.column ?? "—",
      customId: `lb-column-${i}`,
      disabled: true,
      onClick: () => openSlideOver(i),
      editPermissions: "campaign:basic_config:update",
      onChange: () => {},
    },
  ]);

  return (
    <Fields title="Boosting">
      <SlideOverFieldNamedRow
        inputType="number"
        onAccept={(value) =>
          patchBoosting({ boostingWeight: parseNumberOrZero(String(value)) })
        }
        namedRowTitle="Boosting influence"
        permissionsGroup="campaign:basic_config"
        slideOverValue={currentBoosting.boostingWeight}
        {...boostingNumberProps}
      />

      <LimitNumberField
        rowTitle="Boostable products limit"
        switchTitle="Top products only"
        numberTitle="Last boostable product"
        value={currentBoosting.limitRegion}
        onAccept={(value) => patchBoosting({ limitRegion: value })}
        permissionsGroup="campaign:basic_config"
      />

      <LimitNumberField
        rowTitle="Position raise limit"
        switchTitle="Limit raise"
        numberTitle="Maximal raise"
        value={currentBoosting.limitMovement}
        onAccept={(value) => patchBoosting({ limitMovement: value })}
        permissionsGroup="campaign:basic_config"
      />

      <FieldsGroupWrapper
        fieldsType="linkedBoosting"
        initFieldsCount={linkedCampaigns.length}
        initConfig={initConfig}
        onClose={closeSlideOver}
        addFieldHandler={addFieldHandler}
        removeFieldHandler={removeFieldHandler}
        submitFieldHandler={submitFieldHandler}
        fieldRender={fieldRender}
        importantFields={["name"]}
        allowSubmit={allowSubmit}
      >
        <LinkedBoostingSlideOver
          key={isNewDraft ? "new" : (editingIndex ?? "edit")}
          open={slideOverOpen}
          onClose={() => setSlideOverOpen(false)}
          columnOptions={columnOptions}
          customFields={customFields}
          initialLink={isNewDraft ? undefined : currentLink}
          availableCampaigns={
            isNewDraft ? getAvailableForNewDraft() : undefined
          }
          onSubmit={isNewDraft ? handleNewDraftSubmit : handleExistingUpdate}
          onCreateCopy={isNewDraft ? undefined : handleCreateCopy}
        />
      </FieldsGroupWrapper>
    </Fields>
  );
};
